import React from 'react';
import { Button, Col, Form, Row, Typography } from 'components';
import { FormModal } from 'components/common';
import { useEditPricing } from 'feature/brandTabs/pricingAndSuppliers/pricing/edit/useEditPricing';
import { Input, InputNumber } from 'antd';
import { preventNonNumericValue, webSiteUrlRegex } from 'utils/helpers';
import { CloseIcon, DoneIcon, PercentageIcon } from 'components/common/icons';
import { WebsiteItemCard } from 'feature/brandTabs/pricingAndSuppliers/pricing/edit/WebsiteItemCard';
import { PricingRuleType } from 'typings/form/brand/pricingAndSupplier';

const { Text } = Typography;
const { Item } = Form;

export interface IEditPricing {
  modalState: boolean;
  onModalStateChange: () => void;
  dataToEdit?: PricingRuleType;
}

export const EditPricing = React.memo((props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const {
    onFinish,
    contextHolder,
    disableSaveBtn,
    editId,
    form,
    isLoading,
    isChanged,
    websites,
    clearFieldValidation,
    onWebsiteItemRemove,
    onWebsiteItemCancel,
    onWebsiteItemEdit,
    onWebsiteItemSave,
    onWebsiteSave,
    onWebsiteCancel,
    onCancel,
  } = useEditPricing({ onModalStateChange, modalState, dataToEdit });
  return (
    <>
      {contextHolder}
      <FormModal
        form={form}
        title={'Update Pricing Rules'}
        okText={'Save'}
        open={modalState}
        isLoading={isLoading}
        onCancel={onCancel}
        onSubmit={onFinish}
        disableOkBtn={disableSaveBtn}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Item
              label={'Margin'}
              name={'markup'}
              rules={[
                {
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Margin can have up to 2 decimal points.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
              />
            </Item>
          </Col>

          <Col span={6}>
            <Item
              label={'Margin Range'}
              name={'minMargin'}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Min margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Min margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Min margin can have up to 2 decimal points.'));
                    }
                    const maxMargin = getFieldValue('maxMargin');
                    if (maxMargin !== undefined && maxMargin !== null && num > maxMargin) {
                      return Promise.reject(new Error('Min margin cannot be greater than Max margin.'));
                    }
                    clearFieldValidation(form, 'maxMargin');
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
                placeholder={'Min'}
              />
            </Item>
          </Col>

          <Col span={6}>
            <Item
              label={<span style={{ display: 'none' }}></span>}
              name={'maxMargin'}
              rules={[
                ({ getFieldValue }) => ({
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    const num = parseFloat(value);
                    if (isNaN(num)) {
                      return Promise.reject(new Error('Max margin must be a valid number.'));
                    }
                    if (num < 0) {
                      return Promise.reject(new Error('Max margin must be a positive number.'));
                    }
                    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
                      return Promise.reject(new Error('Max margin can have up to 2 decimal points.'));
                    }

                    const minMargin = getFieldValue('minMargin');
                    if (minMargin !== undefined && num < minMargin) {
                      return Promise.reject(new Error('Max margin cannot be less than Min margin.'));
                    }
                    clearFieldValidation(form, 'minMargin');
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber<number>
                controls={false}
                size={'large'}
                onBeforeInput={(event: any) => preventNonNumericValue(event)}
                style={{ width: '100%' }}
                suffix={<PercentageIcon />}
                placeholder={'Max'}
              />
            </Item>
          </Col>

          <Col span={24}>
            <Text style={{ color: '#05796B', fontWeight: 800 }}>Competitor Pricing</Text>
          </Col>

          {!!websites.length && (
            <Col span={24}>
              <Row gutter={[0, 6]}>
                {websites.map((_website) => (
                  <WebsiteItemCard
                    key={_website.id}
                    data={_website}
                    isEditeMode={_website.id === editId}
                    onWebsiteItemRemove={onWebsiteItemRemove}
                    onWebsiteItemEdit={onWebsiteItemEdit}
                    onWebsiteItemSave={onWebsiteItemSave}
                    onWebsiteItemCancel={onWebsiteItemCancel}
                  />
                ))}
              </Row>
            </Col>
          )}

          <Col span={24}>
            <Item
              name="website"
              label={'Website'}
              rules={[{ pattern: webSiteUrlRegex, message: 'Please enter a valid website URL.' }]}
            >
              <Input size={'large'} />
            </Item>
          </Col>

          <Col span={24}>
            <Row gutter={4} justify={'end'}>
              <Col>
                <Button
                  disabled={!isChanged}
                  color="default"
                  variant="filled"
                  onClick={onWebsiteCancel}
                  icon={<CloseIcon />}
                />
              </Col>
              <Col>
                <Button onClick={onWebsiteSave} disabled={!isChanged} type={'primary'} icon={<DoneIcon />} />
              </Col>
            </Row>
          </Col>
        </Row>
      </FormModal>
    </>
  );
});
